import * as React from 'react';
import { FormEvent } from 'react';
import { useDeviceType } from '@cian/ui-kit';
import { Textarea } from '../Textarea';
import { Modal } from '../Modal';
import * as styles from './styles.css';
import { Input, PhoneInput } from '../Inputs';
import { IRetrieveVacancyResponseV2 } from '../../common/repositories/job-vacancies-backend/entities/vacancies/RetrieveVacancyResponseV2';
import { ApplyHeader } from './components/ApplyHeader';
import { FormField } from '../FormField';
import { Typography } from '../Typography';
import { SuccessScreen } from './components/SuccessScreen';
import { IInputsState } from '../../types/form';
import { AgreementCheckbox } from '../PdaAgreementCheckbox/AgreementCheckbox';
import { Button } from '../Button';
import { ApplyError } from './components/ApplyError';
import { StickyActions } from '@cian/frontend-e2e-deal-component';
import cn from 'clsx';

interface IRecommendJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  vacancy: IRetrieveVacancyResponseV2;
  inputValues: IInputsState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (arg: { [name: string]: any }) => void;
  isLoading: boolean;
  isFileUploading: boolean;
  errors: { [key: string]: string };
  isSubmitted: boolean;
  children?: React.ReactNode;
}

export const RecommendJobModal: React.FC<IRecommendJobModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  vacancy,
  inputValues,
  onChange,
  isLoading,
  isFileUploading,
  errors,
  isSubmitted,
  children,
}) => {
  const deviceType = useDeviceType();

  const stickyClassNames = {
    actionArea: cn(styles['action-area'], styles[deviceType]),
    actionAreaWhenStuck: styles['action-area-when-sticky'],
    actionWrapperWhenStuck: styles['action-wrapper-when-sticky'],
  };

  const handlerChange = (name: string, value: string) => {
    onChange({ [name]: value });
  };

  const onSubmitHelper = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  if (errors?.apply) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} type={deviceType === 'desktop' ? 'window' : 'curtain'}>
        <ApplyError title={errors.apply} onClose={onClose} />
      </Modal>
    );
  }

  if (isSubmitted) {
    return (
      <Modal onClose={onClose} isOpen={isOpen} header={<div />} fullScreen={deviceType !== 'desktop'}>
        <SuccessScreen onClose={onClose} type="recommend" />
      </Modal>
    );
  }

  return (
    <Modal
      header={
        <ApplyHeader
          subtitle={'Рекомендовать друга на вакансию'}
          title={deviceType === 'desktop' ? vacancy.title : ''}
        />
      }
      onClose={onClose}
      isOpen={isOpen}
      fullScreen={deviceType !== 'desktop'}
    >
      {deviceType !== 'desktop' && <ApplyHeader title={vacancy.title} />}

      <form className={cn(styles.form, styles[deviceType])} onSubmit={onSubmitHelper}>
        <div className={cn(styles.inputs, styles[deviceType])}>
          <div className={styles.formGroup}>
            <Typography.Title level={6}>Твои контакты</Typography.Title>
            <div className={styles.inputsGroup}>
              <FormField errorText={errors['referer.fullName']}>
                <Input
                  invalid={!!errors['referer.fullName']}
                  placeholder="Фамилия и имя"
                  name={'referer.fullName'}
                  value={inputValues.referer?.fullName}
                  disabled={isLoading}
                  onChange={value => onChange({ referer: { fullName: value } })}
                />
              </FormField>

              <FormField errorText={errors['referer.email']}>
                <Input
                  invalid={!!errors['referer.email']}
                  placeholder="Email"
                  name={'referer.email'}
                  value={inputValues.referer?.email}
                  disabled={isLoading}
                  onChange={value => onChange({ referer: { email: value } })}
                />
              </FormField>
            </div>
          </div>

          <div className={styles.formGroup}>
            <Typography.Title level={6}>Контакты друга</Typography.Title>
            <div className={styles.inputsGroup}>
              <FormField errorText={errors['fullName']}>
                <Input
                  invalid={!!errors['fullName']}
                  placeholder="Фамилия и имя"
                  name={'fullName'}
                  value={inputValues.fullName}
                  disabled={isLoading}
                  onChange={value => handlerChange('fullName', value)}
                />
              </FormField>

              <FormField errorText={errors['email']}>
                <Input
                  invalid={!!errors['email']}
                  placeholder="Email"
                  name={'email'}
                  value={inputValues.email}
                  disabled={isLoading}
                  onChange={value => handlerChange('email', value)}
                />
              </FormField>

              <FormField errorText={errors['phone']}>
                <PhoneInput
                  invalid={!!errors['phone']}
                  placeholder="Телефон"
                  name={'phone'}
                  value={inputValues.phone}
                  disabled={isLoading}
                  onChange={value => handlerChange('phone', value)}
                />
              </FormField>

              <FormField errorText={errors['additionalContact']}>
                <Input
                  invalid={!!errors['additionalContact']}
                  placeholder="Имя в Telegram, через @"
                  name={'additionalContact'}
                  value={inputValues.additionalContact}
                  disabled={isLoading}
                  onChange={value => handlerChange('additionalContact', value)}
                />
              </FormField>

              <FormField errorText={errors['cvUrl']}>
                <Input
                  invalid={!!errors['cvUrl']}
                  placeholder="Ссылка на резюме"
                  name={'cvUrl'}
                  value={inputValues.cvUrl}
                  disabled={isLoading}
                  onChange={value => handlerChange('cvUrl', value)}
                />
              </FormField>

              {children && <FormField errorText={errors['cvFile']}>{children}</FormField>}

              <div className={styles.fullWidth}>
                <FormField errorText={errors['comment']}>
                  <Textarea
                    placeholder="Комментарий (необязательно)"
                    hasCounter
                    maxLength={5000}
                    name={'comment'}
                    value={inputValues.comment || ''}
                    onChange={e => handlerChange('comment', e.target.value)}
                    disabled={isLoading}
                  />
                </FormField>
              </div>
            </div>
          </div>

          <div className={styles.checkbox}>
            <FormField errorText={errors['agreement.fileHash']}>
              <AgreementCheckbox
                id="agreement"
                isError={!!errors['agreement.fileHash']}
                isChecked={!!inputValues.agreement}
                setChecked={val => onChange({ agreement: val })}
                personalDataProcessingPolicyUrl={vacancy.agreement.fileUrl}
                isDisabled={isLoading}
                confidentialityPolicyUrl={''}
                agreementRecommendationUrl={vacancy.agreementRec.fileUrl}
              />
            </FormField>
          </div>
        </div>
        <StickyActions
          deviceTypesWithSticky={['tablet', 'phone']}
          position={'sticky'}
          stickyClassNames={stickyClassNames}
        >
          <div className={styles.submit}>
            <Button type="submit" loading={isLoading} disabled={isFileUploading} fullWidth>
              Отправить
            </Button>
          </div>
        </StickyActions>
      </form>
    </Modal>
  );
};

RecommendJobModal.displayName = 'ApplyJobModal';
